import style from "./invite-to-account-emails.module.css";
import { CloseIcon } from "frontend/icons/close";
import SimpleBar from "simplebar-react";
import { useEffect, useRef } from "react";
import EmailInput from "./email-input";
import EmailInputWithDropdown from "./email-input-with-dropdown";
import { InviteToAccountErrorType } from "./types";

function focusNextInput(targetIndex: number) {
  setTimeout(() => {
    // Get all email inputs in order and focus the target index
    const container = document.getElementById("emails-list");
    if (container) {
      const inputs = container.querySelectorAll("input") as NodeListOf<HTMLInputElement>;
      if (inputs[targetIndex]) {
        inputs[targetIndex].focus();
      }
    }
  }, 0);
}

export default function InviteToAccountEmails({
  emails,
  changeEmail,
  isSending,
  isCreatedFromMonday,
  onRemoveEmail,
  isValidEmail,
}: {
  emails: Record<string, string>;
  changeEmail: (id: string, value: string, isLastEmailInput: boolean) => void;
  onRemoveEmail: (id: string) => void;
  isSending: boolean;
  isCreatedFromMonday: boolean;
  isValidEmail: (email: string) => { isValid: boolean; errorType: InviteToAccountErrorType };
}) {
  const emailsList = Object.keys(emails);
  const simpleBarRef = useRef<any>(null);
  const previousEmailCountRef = useRef(emailsList.length);

  const showRemoveButton = emailsList.length > 1 && !isSending;

  // Scroll to bottom only when a new email input is added (not when removed)
  useEffect(() => {
    const currentCount = emailsList.length;
    const previousCount = previousEmailCountRef.current;

    if (currentCount > previousCount && simpleBarRef.current) {
      const scrollElement = simpleBarRef.current.contentWrapperEl;
      scrollElement.scrollTop = scrollElement.scrollHeight;
    }

    previousEmailCountRef.current = currentCount;
  }, [emailsList.length]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, id: string, index: number) => {
    const target = event.target as HTMLInputElement;
    const value = target.value.trim();
    const isLastInput = index === emailsList.length - 1;

    if (event.key === ",") {
      event.preventDefault();

      // Only create new field or move focus if there's content
      if (value) {
        changeEmail(id, value, isLastInput);
        focusNextInput(index + 1);
      }
    } else if (event.key === "Enter" && value) {
      event.preventDefault();

      changeEmail(id, value, isLastInput);
      focusNextInput(index + 1);
    }
  };

  return (
    <div id="emails-list" className={style.emailInvitesContainer}>
      <SimpleBar ref={simpleBarRef} className={style.emailsScrollContainer}>
        <div className={style.emailInputsContainer}>
          {emailsList.map((id, index) => {
            return (
              <div className={style.inputContainer} key={id}>
                {isCreatedFromMonday ? (
                  <EmailInputWithDropdown
                    id={id}
                    value={emails[id]}
                    disabled={isSending}
                    onChange={(value) => changeEmail(id, value, index === emailsList.length - 1)}
                    onKeyDown={(event) => handleKeyDown(event, id, index)}
                    isValidEmail={isValidEmail}
                  />
                ) : (
                  <EmailInput
                    id={id}
                    value={emails[id]}
                    disabled={isSending}
                    onChange={(value) => changeEmail(id, value, index === emailsList.length - 1)}
                    onKeyDown={(event) => handleKeyDown(event, id, index)}
                    isValidEmail={isValidEmail}
                  />
                )}
                <div className={style.removeEmailButtonContainer}>
                  {showRemoveButton ? (
                    <CloseIcon className={style.removeEmailButton} onClick={() => onRemoveEmail(id)} />
                  ) : (
                    <span className={style.removeEmailButtonPlaceholder} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </SimpleBar>
    </div>
  );
}
